<script setup>
import { computed } from 'vue'

const props = defineProps({
  basePlanPrice: { type: Number, required: true },
  silverPlanPrice: { type: Number, required: true },
  goldPlanPrice: { type: Number, required: true },
  basePlanOffered: { type: Boolean, default: true },
  silverPlanOffered: { type: Boolean, default: true },
  goldPlanOffered: { type: Boolean, default: true },
})

const totalRevenue = computed(() => {
  // Simple estimation: 10 students per enabled plan
  let revenue = 0

  if (props.basePlanOffered) {
    revenue += props.basePlanPrice * 10
  }
  if (props.silverPlanOffered) {
    revenue += props.silverPlanPrice * 10
  }
  if (props.goldPlanOffered) {
    revenue += props.goldPlanPrice * 10
  }

  return Math.round(revenue)
})
</script>

<template>
  <section id="pricing-summary" class="mb-8">
    <div class="rounded-2xl border border-line bg-surface p-5 md:p-8">
      <h3 class="mb-6 text-xl font-bold text-ink">Pricing Summary & Revenue Projection</h3>
      <div class="grid grid-cols-1 gap-6 md:grid-cols-3">
        <!--
          No library-only column. It is not sold on its own, so projecting
          revenue from it promised a tutor income from a plan nobody can buy.
          Its price is still stored and still counted in the total below if the
          tutor set one before the plan was withdrawn.
        -->
        <!-- Bindle Live, on the tier group tuition used. -->
        <div :class="[
          'rounded-xl p-6 transition-all',
          silverPlanOffered ? 'bg-live-soft' : 'bg-surface-2 opacity-50'
        ]">
          <div class="flex items-center justify-between mb-2">
            <span :class="['text-sm font-medium', silverPlanOffered ? 'text-muted' : 'text-subtle']">Bindle Live</span>
            <span :class="['material-symbols-outlined', silverPlanOffered ? 'text-live-soft-text' : 'text-subtle']">sensors</span>
          </div>
          <p :class="['text-3xl font-bold', silverPlanOffered ? 'text-ink' : 'text-subtle']">
            £{{ silverPlanOffered ? silverPlanPrice : 0 }}
          </p>
          <p :class="['text-xs mt-1', silverPlanOffered ? 'text-muted' : 'text-subtle']">
            {{ silverPlanOffered ? 'weekly subscription' : 'not offered' }}
          </p>
        </div>

        <!-- Gold Plan -->
        <div :class="[
          'rounded-xl p-6 transition-all',
          goldPlanOffered ? 'bg-brand-soft' : 'bg-surface-2 opacity-50'
        ]">
          <div class="flex items-center justify-between mb-2">
            <span :class="['text-sm font-medium', goldPlanOffered ? 'text-muted' : 'text-subtle']">1:1 Tutoring</span>
            <span :class="['material-symbols-outlined', goldPlanOffered ? 'text-brand' : 'text-subtle']">workspace_premium</span>
          </div>
          <p :class="['text-3xl font-bold', goldPlanOffered ? 'text-ink' : 'text-subtle']">
            £{{ goldPlanOffered ? goldPlanPrice : 0 }}
          </p>
          <p :class="['text-xs mt-1', goldPlanOffered ? 'text-muted' : 'text-subtle']">
            {{ goldPlanOffered ? 'weekly subscription' : 'not offered' }}
          </p>
        </div>

        <!-- Total Revenue -->
        <div class="rounded-xl bg-success-bg p-6">
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-muted">Est. Weekly</span>
            <span class="material-symbols-outlined text-success">trending_up</span>
          </div>
          <p class="text-3xl font-bold text-ink">£{{ totalRevenue }}</p>
          <p class="mt-1 text-xs text-muted">with 10 students</p>
        </div>
      </div>
    </div>
  </section>
</template>
